import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Upload, MonitorSmartphone, Share2, ChevronDown, Menu, X } from 'lucide-react';
import logoNagy from '../assets/logos/logonagy.png';
import icon from '../assets/logos/icon.png';

function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  
  // FAQ state
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const faqData = t('landing.faq.items', { returnObjects: true });
  const featureItems = t('landing.features.items', { returnObjects: true });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedPrivacy) {
      setSubmitStatus({ type: 'error', message: t('landing.contact.errors.acceptPrivacy') });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm)
      });

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('landing.contact.errors.invalidServerResponse'));
      }

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: t('landing.contact.success.submitted') });
        setContactForm({ name: '', email: '', message: '' });
        setAcceptedPrivacy(false);
      } else {
        setSubmitStatus({ type: 'error', message: data.error || t('landing.contact.errors.submitFailed') });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({ type: 'error', message: error.message || t('landing.contact.errors.connectionFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src={icon} alt="VideoSync" className="h-8" />
            <span className="text-xl font-bold text-gray-800">{t('common.brandName')}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              onClick={() => scrollToSection('features')}
              className="text-gray-600 hover:text-sky-600 transition"
            >
              {t('landing.nav.features')}
            </button>
            {/* <button
              onClick={() => scrollToSection('pricing')}
              className="text-gray-600 hover:text-sky-600 transition"
            >
              Árak
            </button> */}
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-600 hover:text-sky-600 transition"
            >
              {t('landing.nav.contact')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-gray-600 hover:text-sky-600 transition"
            >
              {t('landing.nav.faq')}
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50"
            >
              {t('common.buttons.login')}
            </button>
          </div>

          {/* Hamburger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-600 hover:text-sky-600 transition"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-6 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('features')}
                className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-sky-50 hover:text-sky-600 rounded transition"
              >
                {t('landing.nav.features')}
              </button>
              {/* <button
                onClick={() => scrollToSection('pricing')}
                className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-sky-50 hover:text-sky-600 rounded transition"
              >
                Árak
              </button> */}
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-sky-50 hover:text-sky-600 rounded transition"
              >
                {t('landing.nav.contact')}
              </button>
              <button
                onClick={() => scrollToSection('faq')}
                className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-sky-50 hover:text-sky-600 rounded transition"
              >
                {t('landing.nav.faq')}
              </button>
              <button
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="block w-full px-4 py-2 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50"
              >
                {t('common.buttons.login')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 px-6 bg-gradient-to-br from-indigo-100 via-sky-100 to-emerald-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
                {t('landing.hero.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('landing.hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all shadow-lg disabled:opacity-50"
                >
                  {t('common.buttons.login')}
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  className="px-8 py-4 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all shadow-lg disabled:opacity-50"
                >
                  {t('common.buttons.learnMore')}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                
              </p>
            </div>
            <div className="flex justify-center">
              <img src={logoNagy} alt={t('landing.hero.logoAlt')} className="h-80 object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">{t('landing.features.title')}</h2>
          <p className="text-center text-gray-600 mb-12">{t('landing.features.description')}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-indigo-100 mb-4">
                <Upload className="text-indigo-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">{featureItems[0].title}</h3>
              <p className="text-gray-600">
                {featureItems[0].description}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-sky-100 mb-4">
                <Share2 className="text-sky-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">{featureItems[1].title}</h3>
              <p className="text-gray-600">
                {featureItems[1].description}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition">
              <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-emerald-100 mb-4">
                <MonitorSmartphone className="text-emerald-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">{featureItems[2].title}</h3>
              <p className="text-gray-600">
                {featureItems[2].description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <section id="pricing" className="py-20 px-6 bg-gradient-to-br from-indigo-100 via-sky-100 to-emerald-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">Egyszerű, átlátható árazás</h2>
          <p className="text-center text-gray-600 mb-12">Válaszd az igényeidnek megfelelő csomagot</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Ingyenes</h3>
              <p className="text-gray-600 mb-6">Kezdésnek tökéletes</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-800">0 Ft</span>
                <span className="text-gray-600"> /hó</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Max 3 videó feltöltése
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Max 10MB / videó
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Max 2 platform egyszerre
              </li>
                <li className="flex items-center text-gray-400">
                  <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  </span>
                  Elsőbbségi támogatás
                </li>
              </ul>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50"
              >
                Vágj bele
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-4 border-sky-600 transform md:scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-sky-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Népszerű
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2 mt-4">Pro</h3>
              <p className="text-gray-600 mb-6">Tartalomkészítőknek</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-800">2999 Ft</span>
                <span className="text-gray-600"> /hó</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Korlátlan videó feltöltés
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  50MB / videó
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Mind a 4 platform
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Elsőbbségi támogatás
                </li>
              </ul>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50"
              >
                Lássunk hozzá
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Business</h3>
              <p className="text-gray-600 mb-6">Csapatoknak és ügynökségeknek</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-800">Egyedi árazás</span>
                <span className="text-gray-600"></span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Korlátlan videó feltöltés
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  4K videó feltöltés
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  Több felhasználós hozzáférés
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  </span>
                  24/7 dedikált segítségnyújtás
                </li>
              </ul>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50"
              >
                Lépj kapcsolatba velünk
              </button>
            </div>
          </div>
        </div>
      </section> */}

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">{t('landing.contact.title')}</h2>
          <p className="text-center text-gray-600 mb-12">{t('landing.contact.description')}</p>

          <div className="max-w-2xl mx-auto">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 mb-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('landing.contact.name')}</label>
                  <input
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    placeholder={t('landing.contact.namePlaceholder')}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('landing.contact.email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    placeholder={t('landing.contact.emailPlaceholder')}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('landing.contact.message')}</label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    placeholder={t('landing.contact.messagePlaceholder')}
                    rows="5"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none resize-none disabled:bg-gray-100"
                  ></textarea>
                </div>
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="contactPrivacy"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    disabled={isSubmitting}
                    className="mt-1 mr-2 h-4 w-4 text-sky-500 border-gray-300 rounded focus:ring-sky-500 disabled:opacity-50"
                  />
                  <label htmlFor="contactPrivacy" className="text-sm text-gray-700">
                    <Trans
                      i18nKey="landing.contact.privacyConsent"
                      components={{
                        privacyLink: <Link to="/privacy" className="text-sky-600 hover:text-sky-700 underline" target="_blank" rel="noopener noreferrer" />
                      }}
                    />
                  </label>
                </div>
                {submitStatus.message && (
                  <div className={`p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {submitStatus.message}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('common.buttons.sending') : t('common.buttons.sendMessage')}
                </button>
              </form>
            </div>

            {/* Social Links */}
            <div className="bg-gradient-to-br from-indigo-100 via-sky-100 to-emerald-100 p-6 rounded-xl text-center">
              <h4 className="font-semibold text-gray-800 mb-3">{t('landing.contact.followUs')}</h4>
              <div className="flex justify-center space-x-4">
                <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700">Instagram</a>
                <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700">YouTube</a>
                <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">Facebook</a>
                <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-700">TikTok</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-gradient-to-br from-indigo-50 via-sky-50 to-emerald-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-4">{t('landing.faq.title')}</h2>
          <p className="text-center text-gray-600 mb-12">{t('landing.faq.description')}</p>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                      expandedFAQ === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <h4 className="font-semibold mb-4">{t('landing.footer.product')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <button onClick={() => scrollToSection('features')} className="hover:text-white transition">
                    {t('landing.nav.features')}
                  </button>
                </li>
                <li><a href="/terms" className="hover:text-white transition">{t('landing.footer.pricing')}</a></li>
              </ul>
            </div>
            <div className="text-center">
              <h4 className="font-semibold mb-4">{t('landing.footer.about')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/impresszum" className="hover:text-white transition">{t('landing.footer.impressum')}</a></li>
                <li><a href="#contact" className="hover:text-white transition">{t('landing.footer.contact')}</a></li>
                <li><a href="/impresszum" className="hover:text-white transition">{t('landing.footer.blog')}</a></li>
              </ul>
            </div>
            <div className="text-center">
              <h4 className="font-semibold mb-4">{t('landing.footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/privacy" className="hover:text-white transition">{t('landing.footer.privacy')}</a></li>
                <li><a href="/terms" className="hover:text-white transition">{t('landing.footer.terms')}</a></li>
                <li><a href="/platform-access" className="hover:text-white transition">{t('landing.footer.platformAccess')}</a></li>
                <li><a href="/cookie-policy" className="hover:text-white transition">{t('landing.footer.cookiePolicy')}</a></li>
                <li>
                  <button 
                    onClick={() => window.reopenCookieBanner && window.reopenCookieBanner()} 
                    className="hover:text-white transition"
                  >
                    {t('landing.footer.cookieSettings')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img src={icon} alt="VideoSync" className="h-6" />
              <span className="font-bold">{t('common.brandName')}</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">{t('common.brandTagline')}</p>
            <p className="text-gray-400 text-sm">
              {t('landing.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
